import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from 'assets/images/icons/close.svg';
import useStore from 'state/state';
import clsx from 'clsx';
import { string } from 'prop-types';
import useIsClickOutside from 'hooks/useIsClickOutside';
import GTMTracker from 'tracking/gtm-tracker';
import ContactFormParser from 'utils/contactFormParser';
import Requests from 'http/Requests';
import ReactHtmlParser from 'react-html-parser';
import styles from './Contact.module.scss';
import Form from './Form/Form';
import Response from './Response/Response';

const Contact = ({ productAlias }) => {
  const appData = useStore((state) => state.appData);
  const setIsContactOpen = useStore((state) => state.setIsContactOpen);
  const setAnimatedBoxEndPosition = useStore(
    (state) => state.setAnimatedBoxEndPosition
  );
  const contactFormData = useStore((state) => state.contactFormData);
  const tintRef = useRef(null);
  const contactBoxRef = useRef();
  const onClickOutside = useIsClickOutside();
  const [animateOut, setAnimateOut] = useState(false);
  const [isSending, setSending] = useState(false);
  const [isError, setError] = useState(false);

  const [isSent, setSent] = useState(false);
  const [fields, setFields] = useState(null);

  const closeContact = () => {
    setAnimateOut(true);
    setTimeout(() => {
      setIsContactOpen(false);
    }, 300);
  };

  const onGotoRetailer = () => {
    GTMTracker.push('Contact', 'RetailerButton');
    window.open(appData.mainData.contactInfo.findRetailerUrl, '_blank');
  };

  const onSendCallback = (event) => {
    if (event === 'error') {
      setError(true);
    } else {
      setSent(true);
    }
  };

  const onSend = (data) => {
    const { formId } = appData.mainData.contactForm;
    Requests.postToUmbracoContactForm(formId, data, onSendCallback);
    setSending(true);
  };

  useEffect(() => {
    onClickOutside.setRef(tintRef);
    onClickOutside.setCallbackFunc(() => {
      closeContact();
    });

    if (contactFormData) {
      setFields(ContactFormParser.getFields(contactFormData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (contactBoxRef.current) {
      const firstChildPosition = contactBoxRef.current.getBoundingClientRect();
      setAnimatedBoxEndPosition(firstChildPosition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.tint} ref={tintRef} />
      <div
        ref={contactBoxRef}
        className={clsx(styles.content, { [styles.fadeOut]: animateOut })}
      >
        <div className={styles.col}>
          <h3 className={styles.header}>
            {appData.mainData.contactForm?.header}
          </h3>
          <p className={styles.description}>
            {ReactHtmlParser(appData.mainData.contactForm?.text)}
          </p>
          <button
            onClick={onGotoRetailer}
            className={styles.moreContactsBtn}
            type="button"
          >
            {appData.mainData.contactInfo.findRetailerButtonLabel}
          </button>
        </div>
        <div className={styles.col}>
          <button
            label="close"
            type="button"
            onClick={closeContact}
            className={styles.close}
          >
            <CloseIcon />
          </button>
          <div className={styles.form}>
            {!isSending && fields && (
              <Form
                productAlias={productAlias}
                onSend={onSend}
                fields={fields}
              />
            )}
            {isSending && (
              <Response
                success={isSent}
                successMessage={contactFormData.messageOnSubmit}
                sendingMessage={appData.mainData.contactForm.sendingFormText}
                error={isError}
                errorMessage={appData.mainData.contactForm.sendErrorMessageText}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

Contact.propTypes = {
  productAlias: string.isRequired,
};

export default Contact;
